/**
 * Client-side S2S (service-to-service) token manager. Obtains a bearer token
 * via SigV4-signed requests to the S2S issuer's API Gateway endpoint (or an
 * injected {@link TokenIssuer}), CACHES it in process memory, and REUSES it
 * for the token's lifetime.
 *
 * CACHED, REUSABLE TOKENS — hub decision D845 (locked 2026-09-18).
 * A caller reuses one token per `(audience, scopes, instanceId)` until shortly
 * before it expires (600 seconds by default — the lifetime is the issuer's,
 * a caller cannot request one). Receivers do NOT enforce single-use on S2S
 * tokens; the `jti` claim stays mandatory as the per-token audit handle.
 *
 * History, so nobody "restores" the wrong thing: 0.13.2 (commit 7720ec02,
 * pull request 484) removed this cache because nodii-auth — the only receiver
 * that ever enforced jti replay — rejected reused tokens with
 * `jti already used`. That patch described single-use as doctrine; no doctrine
 * or decision ever said so. D845 resolves it the other way: callers cache,
 * receivers accept.
 *
 * WHY THE CACHE IS SHAPED THIS WAY
 *  - The early refresh is NON-BLOCKING. Inside the refresh window a caller is
 *    handed the still-valid cached token at once while ONE background mint
 *    runs. Only a cold or expired key waits, behind a single shared in-flight
 *    mint per key. A slow issuer therefore never stalls callers that still
 *    hold a usable token.
 *  - Every mint is BOUNDED (issue 816): `mintTimeoutMs`, default 1500 ms. A
 *    mint that has not answered by then fails as a TRANSIENT
 *    {@link S2STokenMintError} — never a denial, so it never evicts a cached
 *    token — and every caller waiting on it gets that error at once. The
 *    manager races the issuer against its OWN timer, so the bound holds even
 *    for an issuer that ignores cancellation; the request is also aborted so a
 *    cooperating issuer frees its socket. The bound is always below the
 *    10-second join window, so a mint settles before a later caller would
 *    stop joining it; that window remains only as a backstop.
 *  - A transient refresh failure keeps serving the unexpired token and retries
 *    no sooner than 5 seconds later. A DENIAL (HTTP 401 / 403) evicts the entry
 *    and is never cached; a stale token is never served after one.
 *  - Per-entry jitter spreads refreshes so tasks deployed together do not all
 *    hit the issuer (and its global audit lock) in the same second.
 *  - The cache is per manager INSTANCE, in memory only: a bearer token is a
 *    credential and never goes to Redis or disk. Build ONE manager per process
 *    — a manager built per call or per client never hits its cache.
 *    (This is a caller-side credential cache, not a verifier replay store: the
 *    R1 "no in-memory default" rule is about the latter and does not apply.)
 *
 * Lifted originally from provisioning's `server/lib/auth/s2s-auth.ts`.
 */
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-js";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { defaultProvider } from "@aws-sdk/credential-provider-node";

type TokenResponse = {
  access_token?: string;
  token?: string;
  token_type?: string; // "Bearer"
  expires_in?: number; // seconds
  expiresAt?: number; // epoch seconds
  scope?: string;
  audience?: string;
};

export type GetTokenParams = {
  audience: string;
  scopes?: string[];
  instanceId?: string;
};

/** Issued token shape produced by a pluggable {@link TokenIssuer}. */
export type IssuedToken = {
  /** Bearer token string. */
  token: string;
  /** Absolute expiry in epoch seconds. */
  expiresAt: number;
};

/**
 * Pluggable token-issuer protocol. Real impl talks SigV4 to API Gateway
 * (the default `refreshToken` path); tests inject a stub to exercise the
 * cache + TTL refresh behavior deterministically (parity with the Python
 * + Go ports which take the issuer as a constructor arg).
 */
export interface TokenIssuer {
  /**
   * `opts.signal` is aborted when the manager's mint bound
   * (`mintTimeoutMs`) expires. Honour it to release the request; the manager
   * stops waiting at the bound either way.
   */
  issue(
    audience: string,
    scopes: readonly string[] | undefined,
    instanceId: string | undefined,
    opts?: { signal?: AbortSignal },
  ): Promise<IssuedToken>;
}

/**
 * Thrown by the {@link S2STokenManager} constructor when the supplied
 * options are mutually inconsistent. Typed so SDK wrappers can
 * `instanceof`-discriminate config errors the same way they
 * discriminate verifier errors via {@link S2SVerifyError}.
 */
export class S2STokenManagerConfigError extends Error {
  override readonly name = "S2STokenManagerConfigError";
}

/** Common fields shared by both options variants. */
type S2STokenManagerOptionsBase = {
  /** Optional: override credentials (otherwise uses defaultProvider chain) */
  credentialsProvider?: ReturnType<typeof defaultProvider>;
  /** Refresh when token has <= this many seconds remaining. Default 30. */
  refreshSkewSeconds?: number;
  /** Optional: additional headers (e.g. tracing) */
  extraHeaders?: Record<string, string>;
  /**
   * Optional clock injection (epoch milliseconds). Defaults to `Date.now`.
   * Used by tests to advance time deterministically across the refresh
   * boundary; parity with the Python + Go ports' `now_fn` / `NowFn`.
   */
  nowFn?: () => number;
  /**
   * Optional jitter source in `[0, 1)`. Each cached entry refreshes up to
   * 10% of its lifetime EARLIER than `refreshSkewSeconds` alone would, scaled
   * by this value. Defaults to `Math.random`; tests inject `() => 0`.
   */
  jitterFn?: () => number;
  /** Upper bound on cached entries (oldest evicted first). Default 256. */
  maxEntries?: number;
  /**
   * How long ONE mint may take, in real milliseconds, before it fails as a
   * TRANSIENT {@link S2STokenMintError} (issue 816). Default 1500. It covers
   * the whole mint: signing, the request, and reading the response. Zero,
   * negative or non-finite means the default — the bound cannot be switched
   * off. Values above 9000 are clamped to 9000 with a warning, so a mint
   * always settles before the 10-second single-flight join window, after
   * which a later caller would stop joining it.
   *
   * On the SigV4 path the constructor starts the AWS credential lookup at
   * once (errors swallowed), so a cold lookup — instance metadata, STS — is
   * paid before the first mint instead of inside its bound. The default
   * provider memoizes; a custom `credentialsProvider` that does not is
   * called once more than before (once to warm, once per mint).
   */
  mintTimeoutMs?: number;
  /**
   * Called when a BACKGROUND refresh fails (the caller was already served a
   * valid cached token, so nothing throws). Defaults to a `console.warn` that
   * never includes the token. `denied` is true for an issuer denial, which
   * also evicts the entry.
   */
  onRefreshError?: (info: {
    audience: string;
    denied: boolean;
    error: unknown;
  }) => void;
};

/**
 * Discriminated union — at compile time, callers must either supply
 * `tokenIssuer` OR `apiBaseUrl + region`; omitting both is a type
 * error. Preserves the DX guarantee the SigV4-only path had before
 * the test-injection seam was added.
 */
export type S2STokenManagerOptions = S2STokenManagerOptionsBase &
  (
    | {
        /** Pluggable issuer; when set, the manager bypasses SigV4 entirely. */
        tokenIssuer: TokenIssuer;
        apiBaseUrl?: string;
        region?: string;
      }
    | {
        tokenIssuer?: never;
        /** Full URL to your API Gateway stage base. */
        apiBaseUrl: string;
        /** Region for API Gateway. */
        region: string;
      }
  );

/**
 * A token mint failed at the issuer. `transient` separates "try again"
 * (network, HTTP 5xx, 408, 429) from a DENIAL (HTTP 401 / 403 and other 4xx):
 * a denial evicts the cached entry and is never retried against a stale token.
 * A mint that exceeds `mintTimeoutMs` is transient with no `status`; its
 * `cause` is a `TimeoutError` `DOMException`. The `S2STokenMintError` itself
 * is the abort reason handed to the issuer's signal.
 */
export class S2STokenMintError extends Error {
  override readonly name = "S2STokenMintError";
  /** HTTP status from the issuer, when there was a response. */
  readonly status?: number;
  readonly transient: boolean;
  constructor(
    message: string,
    opts: { status?: number; transient: boolean; cause?: unknown },
  ) {
    super(
      message,
      opts.cause === undefined ? undefined : { cause: opts.cause },
    );
    this.status = opts.status;
    this.transient = opts.transient;
  }
}

/**
 * What the DEFAULT refresh-failure log line says about an error. An issuer
 * response is reduced to its HTTP status: the mint error's message carries the
 * raw response body (for the caller to log, redacted, if it wants to), and the
 * library must not print a body it does not control.
 */
function describeForLog(error: unknown): string {
  if (error instanceof S2STokenMintError && error.status !== undefined) {
    return `issuer responded HTTP ${error.status}`;
  }
  return error instanceof Error ? error.message : String(error);
}

/** True for a typed issuer denial; anything else is treated as transient. */
function isDenial(err: unknown): boolean {
  return err instanceof S2STokenMintError && !err.transient;
}

type CacheEntry = {
  token: string;
  /** Token expiry, epoch ms. */
  expiresAtMs: number;
  /** From this instant a background refresh is due (skew + jitter applied). */
  refreshAtMs: number;
  /** Earliest next background attempt after a transient refresh failure. */
  nextAttemptAtMs: number;
  /**
   * When the running background refresh for this entry STARTED (epoch ms), or
   * 0 when none is running. Time-stamped rather than a boolean so a refresh
   * that hangs cannot pin the entry forever: past {@link STALE_MINT_MS} it no
   * longer counts, and a new one may start.
   */
  refreshStartedAtMs: number;
};

/** A mint in flight for one key, and when it started. */
type InflightMint = { promise: Promise<string>; startedAtMs: number };

const DEFAULT_REFRESH_SKEW_SECONDS = 30;
const DEFAULT_MAX_ENTRIES = 256;
/** Share of the lifetime a refresh may be pulled earlier by jitter. */
const JITTER_FRACTION = 0.1;
/** Back-off between background refresh attempts after a transient failure. */
const REFRESH_RETRY_MS = 5_000;
/**
 * How long a mint may be in flight before callers stop JOINING it and start a
 * fresh one. Single-flight means one hung request would otherwise wedge its key
 * for the life of the process (before caching, every call made its own request,
 * so a recovered issuer served the very next call). Every mint is now bounded
 * by `mintTimeoutMs`, which is always below this, so a mint settles before this
 * rule could apply; it stays as a backstop.
 */
const STALE_MINT_MS = 10_000;
/**
 * Default bound on ONE mint (issue 816). The tightest caller deadline measured
 * in the fleet is 2 seconds; the refresh skew (30 s) and token lifetime (600 s)
 * leave ample room for a timed-out refresh to be retried.
 */
const DEFAULT_MINT_TIMEOUT_MS = 1_500;
/** Ceiling for `mintTimeoutMs`: strictly below the join window. */
const MAX_MINT_TIMEOUT_MS = 9_000;

/** Sorted + de-duplicated; `undefined` and `[]` are the same scope set. */
function stableScopes(scopes?: readonly string[]): string[] {
  return [...new Set(scopes ?? [])].sort();
}

function cacheKey(p: Partial<GetTokenParams> & { audience: string }): string {
  return JSON.stringify({
    audience: p.audience,
    scopes: stableScopes(p.scopes),
    // Part of the key because the issuer stamps it into the token.
    instanceId: p.instanceId ?? "",
  });
}

function parseUrl(url: string) {
  const u = new URL(url);
  return {
    protocol: u.protocol,
    hostname: u.hostname,
    port: u.port ? Number(u.port) : undefined,
    path: u.pathname + u.search,
  };
}

function resolveMintTimeoutMs(requested: number | undefined): number {
  if (
    requested === undefined ||
    !Number.isFinite(requested) ||
    requested <= 0
  ) {
    return DEFAULT_MINT_TIMEOUT_MS;
  }
  if (requested > MAX_MINT_TIMEOUT_MS) {
    console.warn(
      `[@nodii/grpc-auth] S2STokenManager mintTimeoutMs=${requested} is not below the ${STALE_MINT_MS} ms single-flight join window; clamped to ${MAX_MINT_TIMEOUT_MS} ms.`,
    );
    return MAX_MINT_TIMEOUT_MS;
  }
  return requested;
}

export class S2STokenManager {
  private readonly apiBaseUrl: string;
  private readonly region: string;
  private readonly credsProvider: ReturnType<typeof defaultProvider>;
  private readonly extraHeaders?: Record<string, string>;
  private readonly tokenIssuer?: TokenIssuer;
  private readonly refreshSkewMs: number;
  private readonly nowFn: () => number;
  private readonly jitterFn: () => number;
  private readonly maxEntries: number;
  private readonly mintTimeoutMs: number;
  private readonly onRefreshError: NonNullable<
    S2STokenManagerOptions["onRefreshError"]
  >;

  /** Per-instance, in-memory only. Insertion order = eviction order. */
  private readonly cache = new Map<string, CacheEntry>();
  /**
   * Mints in flight, one per key (single-flight) — cold mints AND background
   * refreshes, so a caller whose token expires mid-refresh joins that refresh
   * instead of starting a second mint for the same key.
   */
  private readonly inflight = new Map<string, InflightMint>();
  /**
   * "The issuer's LATEST word wins." Every mint takes the next sequence number
   * when it STARTS. Per key we remember the highest sequence whose answer has
   * been applied (a stored token, a denial, or a `clear()`); a mint may only
   * change the cache when its own sequence is higher. So a slow mint — good OR
   * denied — that settles after a newer one already answered changes nothing:
   * a late token cannot come back, and a late denial cannot evict the newer
   * token or disturb the newer mint still in flight.
   */
  private mintSeq = 0;
  private readonly settledSeq = new Map<string, number>();
  /** Every sequence at or below this is stale for EVERY key (bare `clear()`). */
  private seqFloor = 0;
  private warnedShortLifetime = false;

  constructor(opts: S2STokenManagerOptions) {
    this.tokenIssuer = opts.tokenIssuer;
    if (!this.tokenIssuer) {
      if (!opts.apiBaseUrl || !opts.region) {
        throw new S2STokenManagerConfigError(
          "S2STokenManager requires apiBaseUrl + region when no tokenIssuer is injected",
        );
      }
    }
    this.apiBaseUrl = (opts.apiBaseUrl ?? "").replace(/\/+$/, "");
    this.region = opts.region ?? "";
    this.credsProvider = opts.credentialsProvider ?? defaultProvider();
    this.extraHeaders = opts.extraHeaders;
    this.refreshSkewMs =
      Math.max(0, opts.refreshSkewSeconds ?? DEFAULT_REFRESH_SKEW_SECONDS) *
      1000;
    this.nowFn = opts.nowFn ?? Date.now;
    this.jitterFn = opts.jitterFn ?? Math.random;
    this.maxEntries = Math.max(1, opts.maxEntries ?? DEFAULT_MAX_ENTRIES);
    this.mintTimeoutMs = resolveMintTimeoutMs(opts.mintTimeoutMs);
    if (!this.tokenIssuer) {
      // Warm the credential chain (issue 816): its first lookup can take
      // seconds on a cold process, and would otherwise run inside the first
      // mint's bound. The default provider memoizes, so the first mint reuses
      // this lookup. A failure here is swallowed — the mint reports it.
      void Promise.resolve()
        .then(() => this.credsProvider())
        .catch(() => {});
    }
    this.onRefreshError =
      opts.onRefreshError ??
      ((info) => {
        // SECURITY: never the token, never credentials — audience + reason only.
        console.warn(
          `[@nodii/grpc-auth] S2S token background refresh ${
            info.denied ? "DENIED" : "failed (serving cached token)"
          } for audience=${info.audience}: ${describeForLog(info.error)}`,
        );
      });
  }

  /**
   * Get a valid bearer token, from cache when possible (hub decision D845).
   *
   *  - fresh hit            → cached token, no I/O.
   *  - inside refresh window → cached token AT ONCE + one background refresh.
   *  - cold or expired       → await ONE shared mint for this key.
   */
  async getToken(params: GetTokenParams): Promise<string> {
    const key = cacheKey(params);
    const now = this.nowFn();
    const entry = this.cache.get(key);

    if (entry && now < entry.expiresAtMs) {
      if (now >= entry.refreshAtMs)
        this.refreshInBackground(key, params, entry);
      return entry.token;
    }
    if (entry) this.cache.delete(key); // expired

    // Join a mint already in flight for this key — unless it has been stuck
    // long enough that joining would just inherit its hang.
    const pending = this.inflight.get(key);
    if (pending && now - pending.startedAtMs < STALE_MINT_MS) {
      return pending.promise;
    }
    return this.startMint(key, params, now);
  }

  /** Start ONE mint for `key` and register it so later callers can join it. */
  private startMint(
    key: string,
    params: GetTokenParams,
    now: number,
  ): Promise<string> {
    const seq = ++this.mintSeq;
    const promise = this.mint(params)
      .then((minted) => {
        this.store(key, minted, seq);
        return minted.token;
      })
      .catch((error: unknown) => {
        // A DENIAL on a cold mint counts exactly as one on a background
        // refresh does: a slower mint this one replaced must not land
        // afterwards and put a token back.
        if (isDenial(error)) this.applyDenial(key, seq);
        throw error;
      })
      .finally(() => {
        // Only the CURRENT registration is removed: clear() or a stale-mint
        // replacement may already have put a newer one in this slot.
        if (this.inflight.get(key)?.promise === promise) {
          this.inflight.delete(key);
        }
      });
    this.inflight.set(key, { promise, startedAtMs: now });
    return promise;
  }

  /**
   * Drop cached token(s): everything when called bare, otherwise every entry
   * matching the given `audience` / `scopes` / `instanceId`. A mint already in
   * flight still answers the callers ALREADY waiting on it, but it is detached:
   * it does not repopulate a cleared key, and a caller arriving AFTER clear()
   * starts a fresh mint instead of joining it. (The case that matters: clear()
   * on a key rotation must never hand out a pre-rotation token afterwards.)
   */
  clear(params?: Partial<GetTokenParams>): void {
    if (!params) {
      this.cache.clear();
      this.inflight.clear();
      this.settledSeq.clear();
      this.seqFloor = this.mintSeq; // every mint started so far is now stale
      return;
    }
    const wantScopes = params.scopes
      ? stableScopes(params.scopes).join("\u0000")
      : undefined;
    const keys = new Set([...this.cache.keys(), ...this.inflight.keys()]);
    for (const k of keys) {
      const parsed = JSON.parse(k) as {
        audience: string;
        scopes: string[];
        instanceId: string;
      };
      if (params.audience !== undefined && parsed.audience !== params.audience)
        continue;
      if (
        params.instanceId !== undefined &&
        parsed.instanceId !== params.instanceId
      )
        continue;
      if (
        wantScopes !== undefined &&
        parsed.scopes.join("\u0000") !== wantScopes
      )
        continue;
      this.invalidateKey(k);
    }
  }

  /**
   * Drop `key` and make every mint started so far for it unable to change the
   * cache. Waiters already joined to such a mint are still answered.
   */
  private invalidateKey(key: string): void {
    this.cache.delete(key);
    this.inflight.delete(key);
    this.markSettled(key, this.mintSeq);
  }

  /**
   * The issuer DENIED mint `seq` for `key`. Returns whether the denial was
   * applied. It is ignored when a NEWER mint has already answered — the issuer
   * said yes after it said no. When applied it evicts the cached token, and any
   * OLDER mint still in flight can no longer store; a NEWER mint still in
   * flight is left alone, registered and free to store, because its answer
   * will be the later one.
   */
  private applyDenial(key: string, seq: number): boolean {
    if (seq <= this.settled(key)) return false;
    this.cache.delete(key);
    this.markSettled(key, seq);
    return true;
  }

  private settled(key: string): number {
    return Math.max(this.seqFloor, this.settledSeq.get(key) ?? 0);
  }

  private markSettled(key: string, seq: number): void {
    this.settledSeq.set(key, seq);
    // Keep the map from growing without bound under many distinct keys.
    // Dropping it while raising the floor to the newest sequence makes every
    // mint started so far stale for every key, exactly as a bare clear() does
    // to in-flight mints. Always safe: the worst case is one token that is
    // minted but not cached, or one denial re-learned on the next refresh.
    if (this.settledSeq.size > this.maxEntries * 4) {
      this.settledSeq.clear();
      this.seqFloor = this.mintSeq;
    }
  }

  /**
   * Cache a freshly minted token — unless `clear()`, a denial, or another
   * token from a mint that started LATER has already settled this key.
   */
  private store(
    key: string,
    minted: { token: string; expiresAtMs: number },
    seq: number,
  ): void {
    if (seq <= this.settled(key)) return;
    this.markSettled(key, seq);
    // A slow mint can land AFTER a newer one (a refresh that outlived its
    // token, overtaken by the cold mint that replaced it). Never let the older
    // token overwrite the one that expires later.
    const existing = this.cache.get(key);
    // The same rule covers a refresh that EXTENDS nothing (an issuer that caches
    // server-side hands back the same expiry). The entry stays as it is, so its
    // refresh would be due again on the very next call — back off instead, or
    // the last `refreshSkewSeconds` of every lifetime become one mint per call.
    if (existing && existing.expiresAtMs >= minted.expiresAtMs) {
      existing.nextAttemptAtMs = this.nowFn() + REFRESH_RETRY_MS;
      return;
    }
    const now = this.nowFn();
    const lifetimeMs = minted.expiresAtMs - now;
    if (lifetimeMs <= this.refreshSkewMs) {
      // Too short-lived to be worth holding: every call would be "refresh due".
      if (!this.warnedShortLifetime) {
        this.warnedShortLifetime = true;
        console.warn(
          `[@nodii/grpc-auth] S2S token lifetime (${Math.round(
            lifetimeMs / 1000,
          )}s) is not longer than refreshSkewSeconds (${
            this.refreshSkewMs / 1000
          }s) — tokens are NOT cached; every call mints.`,
        );
      }
      return;
    }
    const jitterMs =
      Math.min(1, Math.max(0, this.jitterFn())) * JITTER_FRACTION * lifetimeMs;
    this.cache.delete(key); // re-insert at the tail (newest)
    this.cache.set(key, {
      token: minted.token,
      expiresAtMs: minted.expiresAtMs,
      refreshAtMs: Math.max(
        now,
        minted.expiresAtMs - this.refreshSkewMs - jitterMs,
      ),
      nextAttemptAtMs: 0,
      refreshStartedAtMs: 0,
    });
    // Bound memory: evict oldest first. This is a SOFT bound — an entry whose
    // refresh is genuinely running is skipped, so the map can briefly exceed
    // `maxEntries` while many keys refresh at once. A refresh stuck past
    // STALE_MINT_MS no longer protects its entry.
    for (const [k, e] of this.cache) {
      if (this.cache.size <= this.maxEntries) break;
      if (k !== key && !this.isRefreshing(e, now)) this.cache.delete(k);
    }
  }

  private isRefreshing(entry: CacheEntry, now: number): boolean {
    return (
      entry.refreshStartedAtMs !== 0 &&
      now - entry.refreshStartedAtMs < STALE_MINT_MS
    );
  }

  /**
   * One background refresh per entry; never throws into the caller. It is
   * registered in `inflight`, so a caller whose token expires while the refresh
   * is still running JOINS it rather than starting a second mint.
   */
  private refreshInBackground(
    key: string,
    params: GetTokenParams,
    entry: CacheEntry,
  ): void {
    const now = this.nowFn();
    if (this.isRefreshing(entry, now) || now < entry.nextAttemptAtMs) return;
    entry.refreshStartedAtMs = now;
    const seq = ++this.mintSeq;
    // A refresh that hangs past STALE_MINT_MS gets REPLACED by a newer one on
    // the same entry. When the old one finally settles it must clear only ITS
    // OWN marker — clearing unconditionally would wipe the replacement's marker
    // and let a third refresh start alongside it.
    const clearOwnMarker = () => {
      if (entry.refreshStartedAtMs === now) entry.refreshStartedAtMs = 0;
    };
    const promise = this.mint(params)
      .then((minted) => {
        clearOwnMarker();
        this.store(key, minted, seq);
        return minted.token;
      })
      .finally(() => {
        if (this.inflight.get(key)?.promise === promise) {
          this.inflight.delete(key);
        }
      });
    this.inflight.set(key, { promise, startedAtMs: now });
    // The caller already has its token: a failure is reported, never thrown.
    promise.catch((error: unknown) => {
      clearOwnMarker();
      const denied = isDenial(error);
      if (denied) {
        // Never serve a stale token after the issuer said no — unless a NEWER
        // mint has already answered, in which case this late denial is
        // ignored. When applied it also stops any OLDER mint still in flight
        // (a slow refresh this one overtook) from putting a token back later.
        this.applyDenial(key, seq);
      } else {
        entry.nextAttemptAtMs = this.nowFn() + REFRESH_RETRY_MS;
      }
      try {
        // The callback is typed `void`, which still admits an `async` function.
        // Its rejected promise would slip past the try/catch, so absorb both.
        void Promise.resolve(
          this.onRefreshError({ audience: params.audience, denied, error }),
        ).catch(() => {});
      } catch {
        // A throwing consumer callback must not become an unhandled rejection
        // (fatal on Node >= 20) from a refresh the caller never awaited.
      }
    });
  }

  /**
   * Mint one token, BOUNDED by `mintTimeoutMs` (issue 816). The issuer is raced
   * against the manager's own timer, so the bound holds even when the issuer
   * ignores the abort; the abort still releases a cooperating issuer's
   * request. On timeout the mint fails as a TRANSIENT {@link S2STokenMintError}
   * and whatever the issuer returns later is discarded — it never reaches the
   * cache, because the store happens in the caller of this method, only on
   * success.
   */
  private async mint(
    params: GetTokenParams,
  ): Promise<{ token: string; expiresAtMs: number }> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const bound = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        const err = new S2STokenMintError(
          `S2S token mint timed out after ${this.mintTimeoutMs} ms`,
          {
            transient: true,
            cause: new DOMException(
              `mint exceeded ${this.mintTimeoutMs} ms`,
              "TimeoutError",
            ),
          },
        );
        controller.abort(err);
        reject(err);
      }, this.mintTimeoutMs);
      // Never keep a process alive just to fail a mint nobody is waiting on.
      (timer as { unref?: () => void }).unref?.();
    });
    const attempt = this.mintOnce(params, controller.signal);
    // The losing side of the race must not become an unhandled rejection.
    attempt.catch(() => {});
    try {
      return await Promise.race([attempt, bound]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * One mint at the issuer, unbounded — {@link mint} bounds it. No caching
   * here; `getToken` owns that. Kept as one method so the two mint paths —
   * injected issuer vs SigV4/API-Gateway — read clearly and stay
   * parity-aligned with the Python + Go ports.
   */
  private async mintOnce(
    params: GetTokenParams,
    signal: AbortSignal,
  ): Promise<{ token: string; expiresAtMs: number }> {
    // Injected issuer path — bypass the SigV4/API-Gateway network entirely.
    if (this.tokenIssuer) {
      const issued = await this.tokenIssuer.issue(
        params.audience,
        params.scopes,
        params.instanceId,
        { signal },
      );
      if (
        typeof issued.expiresAt !== "number" ||
        !Number.isFinite(issued.expiresAt)
      ) {
        throw new Error(
          "S2S token issuer returned a non-numeric expiresAt (epoch seconds required)",
        );
      }
      // An empty token would be cached and sent as "Bearer " for its whole
      // lifetime; nothing on the caller side would ever evict it.
      if (typeof issued.token !== "string" || issued.token.trim() === "") {
        throw new Error("S2S token issuer returned an empty token");
      }
      return { token: issued.token, expiresAtMs: issued.expiresAt * 1000 };
    }

    const url = `${this.apiBaseUrl}/s2s/token`;
    const body = JSON.stringify({
      audience: params.audience,
      scopes: params.scopes,
      instanceId: params.instanceId,
    });

    const signed = await this.signRequest({
      method: "POST",
      url,
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        ...this.extraHeaders,
      },
      body,
    });

    // The fetch-DOWN path (peer unreachable / non-200) is covered in
    // tests/client/s2s-token-manager.test.ts — "fetch network DOWN" (fetch
    // rejects → typed rejection surfaces, no hang / no silent-undefined token)
    // and "propagates upstream HTTP errors" (non-200 → typed error). The
    // call-site annotation below is the gate's auditable escape: its kms-s3
    // surface recognizes only AWS-SDK shapes (.send(new XCommand) /
    // generateDataKey), not a bespoke `fetch` peer call (a documented
    // partial-recall gap in fault-injection-gate.ts), so a faithful fetch-down
    // test cannot register under that surface.
    const fetchInit = {
      method: "POST",
      headers: signed.headers,
      body,
      // Aborted at the mint bound: releases the socket and the body read.
      signal,
    };
    // The fetch-DOWN path (peer unreachable / non-200) is covered in
    // tests/client/s2s-token-manager.test.ts — "fetch network DOWN" (fetch
    // rejects → typed rejection surfaces, no hang / no silent-undefined token)
    // + "propagates upstream HTTP errors" (non-200 → typed error). The trailing
    // fault-injection:allow is the gate's auditable escape: its kms-s3 surface
    // recognizes only AWS-SDK shapes (.send(new XCommand) / generateDataKey),
    // not a bespoke `fetch` peer call (a documented partial-recall gap in
    // fault-injection-gate.ts), so a faithful fetch-down test can't register
    // under that surface.
    const res = await fetch(url, fetchInit); // fault-injection:allow outbound HTTP-to-peer down covered in s2s-token-manager.test.ts (fetch-rejects + non-200 → typed rejection)

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      // SECURITY: do NOT log the response body / token / credentials. The
      // error propagates to the caller, which decides how to log (redacted).
      // 5xx / 408 / 429 = try again; any other status is a DENIAL (D845: a
      // denial evicts the cached entry and is never cached).
      throw new S2STokenMintError(
        `S2S token fetch failed: ${res.status} ${res.statusText} ${text}`,
        {
          status: res.status,
          transient:
            res.status >= 500 || res.status === 408 || res.status === 429,
        },
      );
    }

    const data = (await res.json()) as TokenResponse;

    const token = data.access_token || data.token;
    if (!token) {
      throw new Error(
        `S2S token response missing token field: ${JSON.stringify(data)}`,
      );
    }

    // Expiry drives the cache, so it is validated STRICTLY: a finite number or
    // nothing. (The old cache "worked" against a fake issuer only because an
    // ISO-string `expiresAt` turned the expiry math into NaN.) `expiresAt` is
    // epoch SECONDS; `expires_in` is relative seconds, resolved against the
    // INJECTED clock with a 1-second safety margin.
    const isNum = (v: unknown): v is number =>
      typeof v === "number" && Number.isFinite(v);
    let expiresAtMs: number;
    if (isNum(data.expiresAt)) {
      expiresAtMs = data.expiresAt * 1000;
    } else if (isNum(data.expires_in)) {
      expiresAtMs = this.nowFn() + Math.max(0, data.expires_in - 1) * 1000;
    } else if (data.expiresAt === undefined && data.expires_in === undefined) {
      throw new Error(
        `S2S token response missing expiration field: ${JSON.stringify({
          ...data,
          access_token: undefined,
          token: undefined,
        })}`,
      );
    } else {
      throw new Error(
        "S2S token response has a non-numeric expiration (expiresAt epoch-seconds or expires_in seconds required)",
      );
    }

    return { token, expiresAtMs };
  }

  private async signRequest(input: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body?: string;
  }): Promise<{ headers: Record<string, string> }> {
    const { hostname, path, protocol, port } = parseUrl(input.url);

    // execute-api expects Host header
    const headers: Record<string, string> = {
      host: hostname,
      ...input.headers,
    };

    const signer = new SignatureV4({
      service: "execute-api",
      region: this.region,
      credentials: this.credsProvider,
      sha256: Sha256,
    });

    const req = new HttpRequest({
      protocol,
      hostname,
      port,
      method: input.method,
      path,
      headers,
      body: input.body,
    });

    const signed = await signer.sign(req);

    // HttpRequest headers values can be string | string[]
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(signed.headers)) {
      out[k] = Array.isArray(v) ? v.join(",") : String(v);
    }

    return { headers: out };
  }
}
